import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2, Lock, ArrowRight, AlertTriangle, Eye, EyeOff,
  CheckCircle2, X,
} from "lucide-react";
import merchantApi from "../../lib/merchantApi";
import { merchantTokenStore } from "../../lib/merchantApi";
import { useMerchantAuth } from "../../context/MerchantAuthContext";

const TABS = [
  { key: "company", label: "Company info", icon: Building2 },
  { key: "account", label: "Account & security", icon: Lock },
];

export default function SettingsPage() {
  const [tab, setTab] = useState("company");
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState(null);

  const refresh = async () => {
    const { data } = await merchantApi.get("/merchant/settings/overview");
    setOverview(data);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try { await refresh(); }
      catch (e) { if (!cancelled) setError(e?.response?.data?.detail || "Could not load settings."); }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const showToast = (msg) => {
    setToast(msg);
    setTimeout(() => setToast(null), 4500);
  };

  if (loading) return <div data-testid="settings-loading" className="text-zinc-500 text-[12px] uppercase tracking-[0.22em]">Loading settings…</div>;
  if (error) return <div data-testid="settings-error" className="text-rose-300 text-[13px]">{error}</div>;

  return (
    <div data-testid="settings-page" className="max-w-5xl">
      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Settings</div>
        <h1 className="font-display mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text leading-tight">
          Settings
        </h1>
        <p className="mt-2 text-[13px] text-zinc-400">Manage your company details and account security.</p>
      </div>

      {/* Tabs */}
      <div data-testid="settings-tabs" className="mt-8 flex gap-1 border-b border-white/[0.06] overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              data-testid={`settings-tab-${key}`}
              className={`relative inline-flex items-center gap-2 px-4 py-3 text-[13px] transition-colors whitespace-nowrap ${
                active ? "text-white" : "text-zinc-500 hover:text-zinc-300"
              }`}
            >
              <Icon className="h-3.5 w-3.5" />
              {label}
              {active && (
                <motion.span
                  layoutId="settings-active-tab"
                  className="absolute left-0 right-0 -bottom-px h-px bg-gradient-to-r from-[#00E5FF] to-[#0055FF]"
                />
              )}
            </button>
          );
        })}
      </div>

      <div className="mt-7">
        <AnimatePresence mode="wait">
          {tab === "company" && (
            <motion.div key="company" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              <CompanyTab company={overview.company} />
            </motion.div>
          )}
          {tab === "account" && (
            <motion.div key="account" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
              <AccountTab account={overview.account} onSuccess={showToast} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            data-testid="settings-toast"
            className="fixed bottom-6 right-6 z-50 max-w-sm rounded-xl border border-emerald-400/30 bg-emerald-400/[0.08] backdrop-blur-md px-4 py-3 text-[12.5px] text-emerald-100 flex items-start gap-2 shadow-2xl"
          >
            <CheckCircle2 className="h-4 w-4 mt-0.5 text-emerald-300 shrink-0" />
            <div className="flex-1 leading-relaxed">{toast}</div>
            <button type="button" onClick={() => setToast(null)} className="text-emerald-200/70 hover:text-white">
              <X className="h-3.5 w-3.5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------- Company tab ----------

const COMPANY_FIELDS = [
  ["legal_name", "Legal name"],
  ["trading_name", "Trading name"],
  ["country_of_incorporation", "Country of incorporation"],
  ["date_of_incorporation", "Date of incorporation"],
  ["company_registration_number", "Registration number"],
  ["tax_vat_number", "Tax / VAT number"],
  ["registered_office", "Registered office"],
  ["primary_website", "Primary website"],
  ["employees", "Employees"],
  ["annual_turnover_eur", "Annual turnover (EUR)"],
  ["primary_contact_name", "Primary contact"],
  ["primary_contact_role", "Contact role"],
  ["primary_contact_email", "Contact email"],
  ["primary_contact_phone", "Contact phone"],
];

function CompanyTab({ company }) {
  const f = company.fields || {};
  return (
    <div data-testid="settings-company">
      {!company.editable && (
        <div className="rounded-xl border border-white/[0.06] bg-white/[0.015] px-4 py-3 mb-5 text-[12.5px] text-zinc-300 flex items-start gap-2">
          <Lock className="h-3.5 w-3.5 mt-0.5 text-zinc-400 shrink-0" />
          <div>Company information is locked once your application is submitted. To make changes, contact your onboarding specialist.</div>
        </div>
      )}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-5 sm:p-6">
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-5">
          {COMPANY_FIELDS.map(([k, label]) => (
            <div key={k} data-testid={`settings-company-${k}`}>
              <dt className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">{label}</dt>
              <dd className="mt-1.5 text-[14px] text-white break-words">
                {formatValue(f[k]) || <span className="text-zinc-600">—</span>}
              </dd>
            </div>
          ))}
        </dl>
      </div>
      {company.editable && (
        <Link
          to={company.edit_target}
          data-testid="settings-company-edit-cta"
          className="group mt-5 inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 transition-colors px-4 py-2 text-[13px] font-medium"
        >
          Edit in onboarding
          <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
        </Link>
      )}
    </div>
  );
}

function formatValue(v) {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "number") return v.toLocaleString();
  return String(v);
}

// ---------- Account tab ----------

function AccountTab({ account, onSuccess }) {
  const navigate = useNavigate();
  const auth = useMerchantAuth();
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [conf, setConf] = useState("");
  const [showCur, setShowCur] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [confirmAll, setConfirmAll] = useState(false);
  const [logoutAllSubmitting, setLogoutAllSubmitting] = useState(false);

  const rules = [
    { key: "len", label: "At least 12 characters", test: (s) => s.length >= 12 },
    { key: "num", label: "Contains a number", test: (s) => /\d/.test(s) },
    { key: "sym", label: "Contains a symbol", test: (s) => /[!@#$%^&*()_+\-={}\[\]:;"'<>,.?/~`|\\]/.test(s) },
    { key: "spc", label: "No spaces", test: (s) => s.length > 0 && !/\s/.test(s) },
  ];
  const passes = rules.map((r) => r.test(next));
  const allOk = passes.every(Boolean);
  const matches = next.length > 0 && next === conf;

  const onChangePw = async (e) => {
    e.preventDefault();
    setError("");
    if (!allOk) { setError("New password doesn't meet the requirements."); return; }
    if (!matches) { setError("New password and confirmation don't match."); return; }
    setSubmitting(true);
    try {
      await merchantApi.post("/merchant/settings/password", {
        current_password: cur, new_password: next,
      });
      setCur(""); setNext(""); setConf("");
      onSuccess("Password changed. You've been signed out of all other devices.");
    } catch (err) {
      setError(err?.response?.data?.detail || "Could not change your password.");
    } finally {
      setSubmitting(false);
    }
  };

  const doLogoutAll = async () => {
    setLogoutAllSubmitting(true);
    try {
      await merchantApi.post("/merchant/settings/logout-all");
    } catch {
      // Even if the call fails, we still drop the local token and bounce.
    }
    try { await auth.logout(); } catch { /* ignore */ }
    merchantTokenStore.clear();
    navigate("/login?reason=logout-all", { replace: true });
  };

  return (
    <div data-testid="settings-account" className="space-y-6">
      {/* Email + last login */}
      <div className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-5">
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Account</div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">Email</div>
            <div data-testid="settings-account-email" className="mt-1.5 text-[14px] text-white">{account.email}</div>
            <div className="mt-1.5 text-[11.5px] text-zinc-500">Email changes aren't supported yet — contact support if you need to update yours.</div>
          </div>
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-zinc-500">Last login</div>
            <div data-testid="settings-account-last-login" className="mt-1.5 text-[14px] text-white">
              {account.last_login_at ? (
                <>
                  {new Date(account.last_login_at).toLocaleString()}
                  {account.last_login_ip && (
                    <span className="text-zinc-500"> · from <span className="ref-mono text-zinc-300">{account.last_login_ip}</span></span>
                  )}
                </>
              ) : (
                <span className="text-zinc-600">No previous sign-in recorded.</span>
              )}
            </div>
            <div className="mt-1.5 text-[11.5px] text-zinc-500">If you don't recognise this, change your password immediately.</div>
          </div>
        </div>
      </div>

      {/* Change password */}
      <form onSubmit={onChangePw} className="rounded-2xl border border-white/[0.06] bg-white/[0.015] p-5" data-testid="settings-password-form">
        <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500">— Change password</div>
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="lg:col-span-2">
            <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Current password</label>
            <div className="relative">
              <input
                type={showCur ? "text" : "password"}
                value={cur}
                onChange={(e) => { setCur(e.target.value); setError(""); }}
                autoComplete="current-password"
                required
                data-testid="settings-current-password"
                className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 px-3 py-2.5 text-[13.5px] text-white pr-10"
              />
              <button type="button" onClick={() => setShowCur((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                {showCur ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">New password</label>
            <div className="relative">
              <input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => { setNext(e.target.value); setError(""); }}
                autoComplete="new-password"
                required
                data-testid="settings-new-password"
                className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 px-3 py-2.5 text-[13.5px] text-white pr-10"
              />
              <button type="button" onClick={() => setShowNext((v) => !v)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300">
                {showNext ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-[11px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5">Confirm new password</label>
            <input
              type={showNext ? "text" : "password"}
              value={conf}
              onChange={(e) => { setConf(e.target.value); setError(""); }}
              autoComplete="new-password"
              required
              data-testid="settings-confirm-password"
              className="w-full rounded-xl bg-white/[0.03] border border-white/[0.08] focus:border-[#00E5FF]/50 focus:outline-none focus:ring-2 focus:ring-[#00E5FF]/20 px-3 py-2.5 text-[13.5px] text-white"
            />
            {conf.length > 0 && !matches && (
              <div className="mt-1.5 text-[11.5px] text-rose-300">Passwords don't match.</div>
            )}
          </div>
        </div>

        <ul data-testid="settings-password-rules" className="mt-4 grid grid-cols-2 gap-x-6 gap-y-1.5">
          {rules.map((r, i) => {
            const ok = passes[i];
            return (
              <li key={r.key} className={`flex items-center gap-2 text-[12px] ${ok ? "text-emerald-300" : "text-zinc-500"}`}>
                <span className={`inline-block h-3 w-3 rounded-full border ${ok ? "border-emerald-400/50 bg-emerald-400/30" : "border-white/[0.10]"}`} />
                {r.label}
              </li>
            );
          })}
        </ul>

        {error && (
          <div data-testid="settings-password-error" className="mt-3 text-[12.5px] text-rose-300">{error}</div>
        )}

        <button
          type="submit"
          disabled={submitting || !cur || !allOk || !matches}
          data-testid="settings-password-submit"
          className="mt-5 inline-flex items-center gap-1.5 rounded-full bg-white text-black hover:bg-zinc-200 disabled:opacity-50 transition-colors px-5 py-2.5 text-[13px] font-medium"
        >
          {submitting ? "Updating…" : "Change password"}
        </button>
      </form>

      {/* Logout all */}
      <div className="rounded-2xl border border-rose-400/15 bg-rose-400/[0.025] p-5" data-testid="settings-logout-all">
        <div className="text-[11px] uppercase tracking-[0.22em] text-rose-200/80">— Sign out everywhere</div>
        <p className="mt-2 text-[13px] text-zinc-300 leading-relaxed">
          Sign out of every device where you're currently signed in — including this one.
          You'll need to sign in again to keep using Neptune.
        </p>
        <button
          type="button"
          onClick={() => setConfirmAll(true)}
          data-testid="settings-logout-all-btn"
          className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-rose-400/30 bg-rose-400/[0.08] text-rose-100 hover:bg-rose-400/[0.15] transition-colors px-4 py-2 text-[13px] font-medium"
        >
          Sign out of all sessions
        </button>
      </div>

      {confirmAll && (
        <div data-testid="settings-logout-all-confirm" className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm px-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-2xl border border-rose-400/30 bg-zinc-950 p-6 shadow-2xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="h-4 w-4 mt-1 text-rose-300 shrink-0" />
              <div>
                <div className="font-display text-[18px] font-semibold text-white tracking-tight">
                  Sign out of all sessions?
                </div>
                <p className="mt-2 text-[13px] text-zinc-400 leading-relaxed">
                  This will sign you out of every device where you're currently signed in, including this one.
                </p>
              </div>
            </div>
            <div className="mt-6 flex flex-wrap gap-2 justify-end">
              <button
                type="button"
                onClick={() => setConfirmAll(false)}
                data-testid="settings-logout-all-cancel"
                className="rounded-full bg-white/[0.06] hover:bg-white/[0.10] text-white transition-colors px-4 py-2 text-[13px]"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={doLogoutAll}
                disabled={logoutAllSubmitting}
                data-testid="settings-logout-all-confirm-btn"
                className="rounded-full border border-rose-400/30 bg-rose-400/[0.08] text-rose-100 hover:bg-rose-400/[0.15] transition-colors px-4 py-2 text-[13px] font-medium disabled:opacity-50"
              >
                {logoutAllSubmitting ? "Signing out…" : "Sign out everywhere"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
